import { SelectOption } from '@bimone/bimtrackreactcomponents';
import { useEffect, useState } from 'react';
import { useLocation, Location, useNavigate } from 'react-router';
import { useSession } from '../../../modules/session';

export interface UseNavBarFeatureSelectorProps {
  isNavBarRoutingEnabled?: boolean;
  features?: string[];
}

export interface UseNavBarFeatureResult {
  states: {
    featureOptions: SelectOption<string>[];
    selectedFeatureOption: SelectOption<string> | undefined;
  };
  actions: {
    handleOnFeatureChange: (selectOptions: SelectOption<string>) => void;
    resetFeatureSelection: () => void;
  };
}

function toOption(path: string): SelectOption<string> {
  return { label: path, value: path };
}

function getFeatureFromPath(location: Location, featuresPath: string[]): string | undefined {
  return location.pathname.split('/').find(pathElement => featuresPath.some(feature => feature === pathElement));
}

export function useNavBarFeatureSelector({
  isNavBarRoutingEnabled = false,
  features = [],
}: UseNavBarFeatureSelectorProps): UseNavBarFeatureResult {
  const location = useLocation();
  const [selectedFeatureOption, setSelectedFeatureOption] = useState<SelectOption<string> | undefined>(undefined);
  const navigate = useNavigate();
  const { project, hub } = useSession();

  const handleOnFeatureChange = (option: SelectOption<string>): void => {
    setSelectedFeatureOption(option);

    if (isNavBarRoutingEnabled) {
      navigate(`/Hubs/${hub?.name}/Projects/${project?.id}/${option.value}`);
    }
  };

  const resetFeatureSelection = (): void => {
    setSelectedFeatureOption(undefined);
  };

  useEffect(() => {
    const featureFromPath = getFeatureFromPath(location, features);

    if (selectedFeatureOption?.value === featureFromPath) {
      return;
    }

    setSelectedFeatureOption(featureFromPath ? toOption(featureFromPath) : undefined);
  }, [features, location, selectedFeatureOption?.value]);

  return {
    states: {
      featureOptions: features.map(toOption),
      selectedFeatureOption,
    },
    actions: {
      handleOnFeatureChange,
      resetFeatureSelection,
    },
  };
}
